import { useLanguage } from "@/context/LanguageContext";
import { SITE } from "@/constants/site";
import { FadeUp, RevealLineInView, RevealImage } from "@/components/animations/Reveal";
import { Scissors, Palette, Sparkles } from "lucide-react";

const ICONS = [Scissors, Palette, Sparkles];

export default function Services() {
    const { t } = useLanguage();
    return (
        <section id="services" className="relative bg-cream py-16 md:py-36 overflow-hidden" data-testid="services">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-4 mb-8">
                    <span className="chapter-num text-lg">{t.services.chapter}</span>
                    <span className="h-[1px] flex-1 bg-gold/40" />
                    <span className="eyebrow">{t.services.eyebrow}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
                    <h2 className="lg:col-span-8 font-display text-ink leading-[0.98] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        <RevealLineInView>{t.services.title}</RevealLineInView>
                    </h2>
                    <FadeUp delay={0.15} className="lg:col-span-4">
                        <p className="text-ink/70 text-base leading-relaxed">{t.services.sub}</p>
                    </FadeUp>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {t.services.cats.map((cat, i) => {
                        const Icon = ICONS[i] || Scissors;
                        return (
                            <FadeUp key={cat.title} delay={0.1 * i}>
                                <article
                                    className="group relative bg-white rounded-3xl border border-gold/20 overflow-hidden hover:border-gold/60 transition-colors duration-500 h-full flex flex-col"
                                    data-testid={`service-card-${i}`}
                                >
                                    {/* Image */}
                                    <div className="relative aspect-[5/4] overflow-hidden">
                                        <RevealImage
                                            src={SITE.images.services[i]}
                                            alt={cat.title}
                                            delay={i * 0.08}
                                            className="w-full h-full"
                                        />
                                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent z-10" />
                                        <div className="absolute top-5 left-5 flex items-center gap-2 text-white z-20">
                                            <span className="text-[0.65rem] tracking-[0.3em] uppercase">0{i + 1}</span>
                                            <span className="h-[1px] w-8 bg-white/70" />
                                        </div>
                                        <div className="absolute bottom-5 left-5 flex items-center gap-3 z-20">
                                            <div className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center">
                                                <Icon className="w-5 h-5 text-gold-deep" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-8 flex-1 flex flex-col">
                                        <h3 className="font-display text-3xl text-ink mb-6">
                                            {cat.title}
                                        </h3>
                                        <ul className="space-y-3">
                                            {cat.items.map((item) => (
                                                <li
                                                    key={item}
                                                    className="flex items-center gap-3 text-ink/80 text-[0.95rem]"
                                                >
                                                    <span className="h-[1px] w-4 bg-gold" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </article>
                            </FadeUp>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
